import React, { useState, useEffect } from 'react';
import { getErrorMessage } from '../utils/errorUtils';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  Fade,
  Grow,
  Dialog,
  DialogContent,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import {
  Emergency as EmergencyIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  Gavel as GavelIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import LegalVerificationPanel, { VerificationResult } from '../components/LegalVerificationPanel';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { PAY_PER_USE_PRICING, CREDIT_VALUE_CZK } from '../config/pricing';

interface ConsultationResponse {
  response: string;
  confidence?: number;
  sources?: string[];
  confidence_level?: string;
  disclaimer?: string;
}

interface LegalDomainInfo {
  domain: string;
  text: string;
  reasoning?: string;
}

interface ConversationMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: string[];
  confidence_level?: string;
  disclaimer?: string;
  verificationResult?: VerificationResult;
  total_tokens?: number;
  processing_time?: number;
}

interface ClarifyingQuestion {
  id: string;
  question: string;
  answer: string;
}

type ConsultationPhase = 'initial' | 'clarifying' | 'final' | 'completed';

interface ProcessingStatus {
  phase: 'initializing' | 'processing_context' | 'ai_thinking' | 'preparing_response' | 'finalizing';
  message: string;
  progress: number;
}

const ConsultationPage: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [category, setCategory] = useState<string>('');
  const [question, setQuestion] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<ConsultationResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [verificationResult, setVerificationResult] = useState<VerificationResult | undefined>(undefined);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const { collapsed } = useSidebar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [followUpQuestion, setFollowUpQuestion] = useState<string>('');
  
  // New state for clarification workflow
  const [consultationPhase, setConsultationPhase] = useState<ConsultationPhase>('initial');
  const [clarifyingQuestions, setClarifyingQuestions] = useState<string[]>([]);
  const [clarifyingAnswers, setClarifyingAnswers] = useState<string[]>([]);
  const [originalQuestion, setOriginalQuestion] = useState<string>('');
  const [legalDomain, setLegalDomain] = useState<string>('');
  const [allLegalDomains, setAllLegalDomains] = useState<string[]>([]);
  const [domainsCount, setDomainsCount] = useState<number>(0);
  const [domainsInfo, setDomainsInfo] = useState<LegalDomainInfo[]>([]);
  
  // Processing status for progress card
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [showProcessingCard, setShowProcessingCard] = useState<boolean>(false);
  const consultationPricing = PAY_PER_USE_PRICING.find(item => item.key === 'consultation');
  const formatCurrency = (value: number) => value.toLocaleString('cs-CZ');
  const creditValue = CREDIT_VALUE_CZK.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Highlight helpers for assistant messages
  const extractTerms = (text: string): string[] => {
    if (!text) return [];
    const tokens = text
      .toLowerCase()
      .split(/[^\p{L}\p{N}]+/u)
      .filter(t => t && t.length >= 3);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of tokens) {
      if (!seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
      if (out.length >= 8) break;
    }
    return out;
  };

  const stripDiacritics = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}+/gu, '');
  const czCharClass = (ch: string) => {
    const map: Record<string, string> = {
      a: 'aá', c: 'cč', d: 'dď', e: 'eéě', i: 'ií', n: 'nň', o: 'oó', r: 'rř', s: 'sš', t: 'tť', u: 'uúů', y: 'yý', z: 'zž'
    };
    const lower = ch.toLowerCase();
    const set = map[lower];
    if (set) return `[${set}]`;
    // digits or other letters
    return escapeRegex(lower);
  };
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const normalizeBase = (term: string) => {
    const t = stripDiacritics(term.toLowerCase());
    // Basic Czech suffix stripping (safe, only if length remains >= 4)
    const suffixes = ['ove','ami','emi','ach','ich','ych','eho','emu','iho','imu','ech','ost','nost','ace','aci','ou','em','om','um','am','im','y','a','u','e','i','o'];
    for (const suf of suffixes.sort((a,b)=>b.length-a.length)) {
      if (t.endsWith(suf) && t.length - suf.length >= 4) {
        return t.slice(0, t.length - suf.length);
      }
    }
    return t;
  };
  const termToPattern = (term: string) => {
    const base = normalizeBase(term);
    const chars = Array.from(base).map(czCharClass).join('');
    // Allow short Czech word-form endings up to 3 chars
    return `\\b${chars}(?:[a-zá-ž]{0,3})?\\b`;
  };
  const boldMatchInMarkdown = (content: string, terms: string[]): string => {
    if (!content || !terms || terms.length === 0) return content;
    const uniq = Array.from(new Set(terms.filter(Boolean)));
    if (uniq.length === 0) return content;
    const pattern = uniq.map(termToPattern).join('|');
    try {
      const re = new RegExp(`(${pattern})`, 'giu');
      // Wrap as <mark>..</mark>; rehypeRaw allows inline HTML
      return content.replace(re, '<mark>$&</mark>');
    } catch {
      return content;
    }
  };

  useEffect(() => {
    // Extract category from URL params
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    if (categoryParam) {
      setCategory(categoryParam);
    }
  }, [location]);

  const handleEmergencyCall = () => {
    // Open emergency contact options
    const emergencyNumbers = [
      { name: 'Policie', number: '158' },
      { name: 'Záchranná služba', number: '155' },
      { name: 'Hasiči', number: '150' },
      { name: 'Integrovaný záchranný systém', number: '112' },
      { name: 'Bezplatná právní pomoc', number: '800 888 388' },
    ];

    const message = emergencyNumbers
      .map(item => `${item.name}: ${item.number}`)
      .join('\n');

    if (window.confirm(`Urgentní kontakty:\n\n${message}\n\nChcete zavolat 112?`)) {
      window.open('tel:112');
    }
  };

  const getCategoryTitle = (cat: string) => {
    const titles = {
      housing: 'Bydlení a nemovitosti',
      work: 'Práce a zaměstnání', 
      family: 'Rodina a vztahy',
      accidents: 'Nehody a pojištění',
      documents: 'Právní dokumenty',
      general: 'Obecné právní otázky'
    };
    return titles[cat as keyof typeof titles] || 'Právní konzultace';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    setError('');
    setResponse(null);
    setOriginalQuestion(question.trim());
    // DO NOT show processing card here - just generate clarifying questions

    try {
      // Use new clarification workflow
      const result = await api.post('/consultation/ask-with-clarification', {
        question: question.trim(),
        category: category || 'general',
        user_id: user?.user_id || null // Pass authenticated user ID or allow anonymous
      });

      // Multi-agent returns direct response, not clarifying questions
      if (result.data.clarifying_questions) {
        // Standard endpoint with clarification workflow
        setClarifyingQuestions(result.data.clarifying_questions);
        setClarifyingAnswers(new Array(result.data.clarifying_questions.length).fill(''));
        setConsultationPhase('clarifying');
      } else {
        // Multi-agent endpoint with direct response
        const newMessage: ConversationMessage = {
          id: Date.now().toString(),
          type: 'assistant' as const,
          content: result.data.response,
          timestamp: new Date(),
          sources: result.data.sources || [],
          disclaimer: result.data.disclaimer,
          confidence_level: result.data.confidence_level,
          total_tokens: result.data.total_tokens || 0,
          processing_time: result.data.processing_time || 0
        };

        setConversationHistory(prev => [...prev, newMessage]);
        setConsultationPhase('completed');
      }
      setConversationId(result.data.conversation_id);
      setLegalDomain(result.data.legal_domain || '');
      setAllLegalDomains(result.data.all_legal_domains || []);
      setDomainsCount(result.data.domains_count || 0);
      setDomainsInfo(result.data.domains_info || []);
      setConsultationPhase('clarifying');
      
      // Clear original question field
      setQuestion('');
    } catch (err: any) {
      console.error('Consultation error:', err);
      setError(getErrorMessage(err, 'Omlouváme se, došlo k chybě při zpracování vaší otázky. Zkuste to prosím znovu.'));
    } finally {
      setLoading(false);
    }
  };

  const handleNewQuestion = () => {
    setQuestion('');
    setResponse(null);
    setError('');
    setVerificationResult(undefined);
    setConversationHistory([]);
    setConversationId(null);
    setFollowUpQuestion('');
    
    // Reset clarification workflow
    setConsultationPhase('initial');
    setClarifyingQuestions([]);
    setClarifyingAnswers([]);
    setOriginalQuestion('');
    setLegalDomain('');
    setAllLegalDomains([]);
    setDomainsCount(0);
    setDomainsInfo([]);
    
    // Reset processing status
    setShowProcessingCard(false);
    setProcessingStatus(null);
  };

  const handleClarificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if all questions are answered
    const unansweredQuestions = clarifyingAnswers.filter(answer => !answer.trim());
    if (unansweredQuestions.length > 0) {
      setError('Prosím odpovězte na všechny doptávací otázky před pokračováním.');
      return;
    }

    setLoading(true);
    setError('');
    setShowProcessingCard(true);
    
    // Realistic status updates based on what backend actually does
    const statusUpdates = [
      { phase: 'initializing' as const, message: 'Připravuji zpracování dotazu...', progress: 15, delay: 0 },
      { phase: 'processing_context' as const, message: 'Zpracovávám kontext a doplňující informace...', progress: 35, delay: 2000 },
      { phase: 'ai_thinking' as const, message: 'AI analyzuje váš dotaz podle českého práva...', progress: 60, delay: 4000 },
      { phase: 'preparing_response' as const, message: 'Připravuji komplexní právní odpověď...', progress: 85, delay: 7000 },
      { phase: 'finalizing' as const, message: 'Dokončuji zpracování...', progress: 95, delay: 9000 }
    ];
    
    // Set initial status
    setProcessingStatus(statusUpdates[0]);
    
    // Schedule status updates
    statusUpdates.slice(1).forEach(update => {
      setTimeout(() => {
        setProcessingStatus({
          phase: update.phase,
          message: update.message,
          progress: update.progress
        });
      }, update.delay);
    });

    try {
      // Send clarification answers to get final response
      const result = await api.post('/consultation/answer-clarification', {
        conversation_id: conversationId,
        answers: clarifyingAnswers,
        language: 'cs'
      });

      // Create conversation messages for display
      const userQuestionMessage: ConversationMessage = {
        id: Date.now().toString(),
        type: 'user',
        content: originalQuestion,
        timestamp: new Date()
      };

      const clarificationMessage: ConversationMessage = {
        id: (Date.now() + 1).toString(),
        type: 'user',
        content: `Doplňující informace:\n${clarifyingQuestions.map((q, i) => `${i + 1}. ${q}\nOdpověď: ${clarifyingAnswers[i]}`).join('\n\n')}`,
        timestamp: new Date()
      };

      const assistantMessage: ConversationMessage = {
        id: (Date.now() + 2).toString(),
        type: 'assistant',
        content: result.data.response,
        timestamp: new Date(),
        sources: result.data.sources,
        confidence_level: result.data.confidence_level,
        disclaimer: result.data.disclaimer
      };

      setConversationHistory([userQuestionMessage, clarificationMessage, assistantMessage]);
      setResponse(result.data);
      setConsultationPhase('final');
      setShowProcessingCard(false);
      
      // Start verification for the final response
      verifyResponse(result.data.response, assistantMessage.id);
      
    } catch (err: any) {
      console.error('Clarification submission error:', err);
      setError(getErrorMessage(err, 'Omlouváme se, došlo k chybě při zpracování vašich odpovědí.'));
      setShowProcessingCard(false);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!followUpQuestion.trim()) return;

    setLoading(true);
    setError('');

    const userMessage: ConversationMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: followUpQuestion.trim(),
      timestamp: new Date()
    };

    setConversationHistory(prev => [...prev, userMessage]);

    try {
      const result = await api.post('/consultation/ask', {
        question: followUpQuestion.trim(),
        category: category || 'general',
        conversation_id: conversationId,
        user_id: user?.user_id || undefined,
        conversation_history: conversationHistory.map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.content
        }))
      });

      const assistantMessage: ConversationMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: result.data.response,
        timestamp: new Date(),
        sources: result.data.sources,
        confidence_level: result.data.confidence_level,
        disclaimer: result.data.disclaimer
      };

      setConversationHistory(prev => [...prev, assistantMessage]);
      setResponse(result.data);
      
      // Start verification for the new response
      verifyResponse(result.data.response, assistantMessage.id);
      
      // Clear follow-up question field
      setFollowUpQuestion('');
    } catch (err: any) {
      console.error('Follow-up error:', err);
      setError(getErrorMessage(err, 'Omlouváme se, došlo k chybě při zpracování vaší otázky.'));
      // Remove user message if error occurred
      setConversationHistory(prev => prev.filter(msg => msg.id !== userMessage.id));
    } finally {
      setLoading(false);
    }
  };

  const verifyResponse = async (responseText: string, messageId?: string) => {
    setIsVerifying(true);
    try {
      const result = await api.post('/consultation/verify', {
        prompt: responseText,
        type: 'factual_check'
      });
      
      // Parse verification response into structured result
      const verificationText = result.data.response;
      const warnings: string[] = [];
      const corrections: string[] = [];
      
      // Simple parsing logic - in production, this would be more sophisticated
      if (verificationText.includes('upozornění') || verificationText.includes('pozor')) {
        warnings.push('Odpověď může vyžadovat dodatečné ověření u právního specialisty');
      }
      
      const mappedConfidence = (response?.confidence_level as 'high' | 'medium' | 'low') || 'medium';
      const confidenceScore = mappedConfidence === 'high' ? 85 : mappedConfidence === 'medium' ? 60 : 35;

      const verificationResult: VerificationResult = {
        isAccurate: !verificationText.toLowerCase().includes('nepřesné'),
        confidence: mappedConfidence,
        confidenceScore,
        warnings,
        corrections,
        verifiedBy: 'AI Legal Verification System',
        verificationTimestamp: new Date().toISOString()
      };
      
      setVerificationResult(verificationResult);
      
      // Update verification result in conversation history if messageId is provided
      if (messageId) {
        setConversationHistory(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, verificationResult } 
            : msg
        ));
      }
    } catch (err) {
      console.error('Verification failed:', err);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      
      <Box sx={{ 
        flex: 1,
        marginLeft: isMobile ? 0 : (collapsed ? '80px' : '280px'),
        paddingTop: isMobile ? '64px' : 0,
        minHeight: '100vh',
        backgroundColor: '#F2F2F2',
        transition: 'margin-left 0.3s ease'
      }}>
        <Box sx={{ 
          px: { xs: 3, sm: 4, md: 6 },
          py: { xs: 4, sm: 6 }
        }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: { xs: 4, md: 6 }
        }}>
          <Box>
            <Typography sx={{ 
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '24px', sm: '28px', md: '32px', lg: '36px' },
              lineHeight: { xs: 1.2, md: 1.17 },
              color: '#1F1F1F',
              mb: { xs: 1, md: 2 }
            }}>
              {getCategoryTitle(category)}
            </Typography>
            <Typography sx={{ 
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '14px', sm: '15px', md: '16px' },
              lineHeight: 1.5,
              color: '#666666'
            }}>
              Zeptejte se na cokoliv, co vás trápí. Odpovím na základě českého práva.
            </Typography>
          </Box>
          
          {/* Emergency Button */}
          <Button
            variant="contained"
            color="error"
            startIcon={<EmergencyIcon />}
            onClick={handleEmergencyCall}
            sx={{
              padding: { xs: '12px 24px', sm: '14px 32px' },
              border: '2px solid #ef5350',
              color: '#ef5350',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,240,240,0.9) 100%)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderRadius: '999px',
              fontSize: { xs: '14px', sm: '16px' },
              fontWeight: 500,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 16px rgba(239, 83, 80, 0.15), 0 2px 8px rgba(239, 83, 80, 0.1)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(105deg, rgba(255,255,255,0) 40%, rgba(255,255,255,0.6) 45%, rgba(255,255,255,0) 50%)',
                transition: 'left 0.8s ease',
                pointerEvents: 'none',
              },
              '&:hover::before': {
                left: '100%',
              },
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,235,238,0.95) 100%)',
                borderColor: '#f44336',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 32px rgba(239, 83, 80, 0.25), 0 4px 16px rgba(239, 83, 80, 0.15)',
              },
              '&:active': {
                transform: 'translateY(0)',
              }
            }}
          >
            Nouze
          </Button>
        </Box>

        {/* Jednoduchý návod – Konzultace (v aplikaci) */}
        {!conversationId && (
          <Box sx={{
            mt: { xs: 2, md: 3 },
            p: { xs: 2.5, sm: 3 },
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(0,0,0,0.06)',
            maxWidth: 900,
            mx: 'auto',
            mb: { xs: 4, sm: 5 }
          }}>
            <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: '#1F1F1F', textAlign: 'center', mb: 1.5 }}>
              Jak postupovat – Právní konzultace
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircleIcon sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>1) Napište dotaz</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Stručně popište problém vlastními slovy. Právní terminologii znát nemusíte.</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircleIcon sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>2) Odpovězte na doplňující otázky</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Systém se doptá na detaily, aby odpověď byla přesná.</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircleIcon sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>3) Získejte odpověď s odkazy</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Odpověď obsahuje odkazy na zákony a praktické kroky, co dělat dál.</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircleIcon sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>4) Vše se ukládá do Dashboardu</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Ke konzultacím se kdykoli vrátíte v Dashboardu a můžete pokračovat.</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Emergency Alert for accidents */}
        {category === 'accidents' && (
          <Alert 
            severity="error" 
            action={
              <Button 
                color="inherit" 
                size="small" 
                startIcon={<PhoneIcon />}
                onClick={handleEmergencyCall}
              >
                Zavolat
              </Button>
            }
            sx={{ 
              mb: { xs: 3, md: 4 },
              borderRadius: { xs: '16px', md: '20px' },
              backgroundColor: '#FFFFFF',
              border: '1px solid #f44336'
            }}
          >
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '13px', md: '14px' },
              lineHeight: 1.43,
              color: '#1F1F1F'
            }}>
              <strong>Urgentní situace?</strong> Pokud někdo potřebuje lékařskou pomoc, zavolejte 155 nebo 112.
            </Typography>
          </Alert>
        )}

        {/* Processing Progress Dialog */}
        <Dialog
          open={showProcessingCard && processingStatus !== null}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }
          }}
          PaperProps={{
            sx: {
              borderRadius: '32px',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.9) 100%)',
              backdropFilter: 'blur(32px)',
              WebkitBackdropFilter: 'blur(32px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#212121',
              boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12), 0 16px 32px rgba(0, 0, 0, 0.10)',
              minWidth: { xs: '320px', sm: '400px' },
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '4px',
                background: 'linear-gradient(90deg, transparent, rgba(211, 47, 47, 0.6), transparent)',
                animation: 'shimmer 3s ease-in-out infinite',
                pointerEvents: 'none',
              },
              '@keyframes shimmer': {
                '0%': {
                  left: '-100%',
                },
                '100%': {
                  left: '100%',
                },
              },
            }
          }}
        >
          <DialogContent sx={{ p: 4 }}>
            {processingStatus && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(31, 31, 31, 0.1) 0%, rgba(31, 31, 31, 0.05) 100%)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    border: '2px solid rgba(31, 31, 31, 0.2)',
                    boxShadow: '0 0 30px rgba(31, 31, 31, 0.15), 0 8px 24px rgba(31, 31, 31, 0.1)',
                    animation: 'pulse 2s ease-in-out infinite',
                    mr: 3,
                    '@keyframes pulse': {
                      '0%, 100%': {
                        opacity: 1,
                        transform: 'scale(1)',
                      },
                      '50%': {
                        opacity: 0.8,
                        transform: 'scale(1.05)',
                      },
                    },
                  }}>
                    {processingStatus.phase === 'initializing' && <AutoAwesomeIcon sx={{ fontSize: 32, color: '#1F1F1F', filter: 'drop-shadow(0 0 8px rgba(31, 31, 31, 0.3))' }} />}
                    {processingStatus.phase === 'processing_context' && <SearchIcon sx={{ fontSize: 32, color: '#1F1F1F', filter: 'drop-shadow(0 0 8px rgba(31, 31, 31, 0.3))' }} />}
                    {processingStatus.phase === 'ai_thinking' && <GavelIcon sx={{ fontSize: 32, color: '#1F1F1F', filter: 'drop-shadow(0 0 8px rgba(31, 31, 31, 0.3))' }} />}
                    {processingStatus.phase === 'preparing_response' && <DescriptionIcon sx={{ fontSize: 32, color: '#1F1F1F', filter: 'drop-shadow(0 0 8px rgba(31, 31, 31, 0.3))' }} />}
                    {processingStatus.phase === 'finalizing' && <CheckCircleIcon sx={{ fontSize: 32, color: '#d32f2f', filter: 'drop-shadow(0 0 8px rgba(211, 47, 47, 0.5))' }} />}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, color: '#212121' }}>
                      Zpracovávám váš dotaz
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#616161' }}>
                      {processingStatus.message}
                    </Typography>
                  </Box>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={processingStatus.progress}
                  sx={{
                    height: 8,
                    borderRadius: 9999,
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    overflow: 'hidden',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 9999,
                      background: '#d32f2f',
                      boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)',
                    },
                    '@keyframes shimmer-progress': {
                      '0%': {
                        backgroundPosition: '-1000px 0',
                      },
                      '100%': {
                        backgroundPosition: '1000px 0',
                      },
                    },
                  }}
                />
                
                {processingStatus.phase === 'processing_context' && (
                  <Fade in={true}>
                    <Typography variant="body2" sx={{ display: 'block', mt: 3, color: '#424242', fontWeight: 500 }}>
                      📊 Analyzuji všechny poskytnuté informace...
                    </Typography>
                  </Fade>
                )}

                {processingStatus.phase === 'ai_thinking' && (
                  <Fade in={true}>
                    <Typography variant="body2" sx={{ display: 'block', mt: 3, color: '#424242', fontWeight: 500 }}>
                      🤔 AI právník zpracovává váš dotaz podle českých zákonů...
                    </Typography>
                  </Fade>
                )}

                {processingStatus.phase === 'preparing_response' && (
                  <Fade in={true}>
                    <Typography variant="body2" sx={{ display: 'block', mt: 3, color: '#424242', fontWeight: 500 }}>
                      ✍️ Formuluji detailní právní odpověď...
                    </Typography>
                  </Fade>
                )}
                
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <CircularProgress
                    size={32}
                    sx={{
                      color: '#1976d2',
                      filter: 'drop-shadow(0 0 8px rgba(25, 118, 210, 0.4))',
                    }}
                  />
                </Box>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Main Content */}
        <Box sx={{
          padding: { xs: '24px 20px', sm: '32px 28px', md: '48px 40px 40px' },
          borderRadius: { xs: '16px', md: '24px' },
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.8) 100%)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.07)',
          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          mb: { xs: 6, md: 8 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
            backgroundSize: '1000px 100%',
            animation: 'shimmer-card 3s infinite',
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 0.5s ease',
          },
          '&:hover::before': {
            opacity: 1,
          },
          '@keyframes shimmer-card': {
            '0%': {
              backgroundPosition: '-1000px 0',
            },
            '100%': {
              backgroundPosition: '1000px 0',
            },
          },
        }}>
          {consultationPhase === 'initial' ? (
            /* Initial Question Form */
            <Box component="form" onSubmit={handleSubmit}>
              <Typography sx={{ 
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: { xs: '20px', sm: '22px', md: '24px' },
                lineHeight: 1.33,
                color: '#1F1F1F',
                mb: { xs: 2, md: 3 }
              }}>
              Popište mi vaši právní situaci
              </Typography>

              {consultationPricing && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  mb: 3,
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderRadius: '14px',
                  px: { xs: 2, sm: 2.5 },
                  py: { xs: 1, sm: 1.2 },
                  boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
                }}>
                  <Chip
                    label={`${formatCurrency(consultationPricing.priceCZK)} Kč`}
                    sx={{
                      backgroundColor: '#1F1F1F',
                      color: '#FFFFFF',
                      fontWeight: 600
                    }}
                  />
                  <Chip
                    label={`${formatCurrency(consultationPricing.credits)} kreditů`}
                    sx={{
                      backgroundColor: 'rgba(231,47,13,0.12)',
                      color: '#e72f0d',
                      fontWeight: 600
                    }}
                  />
                  <Typography sx={{ fontSize: '13px', color: '#424242', fontWeight: 500 }}>
                    1 kredit = {creditValue} Kč. Platba se odečte až po doručení odpovědi.
                  </Typography>
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                rows={6}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Například: Mám problém s pronájmem bytu. Majitel mi neoprávněně zadržel kauci..."
                variant="outlined"
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontSize: '16px',
                    lineHeight: 1.6,
                    background: 'rgba(255, 255, 255, 0.8)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.08)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1F1F1F !important',
                      borderWidth: '1px'
                    }
                  }
                }}
                disabled={loading}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <SendIcon />}
                  disabled={loading || !question.trim()}
                  sx={{
                    padding: '14px 36px',
                    backgroundColor: '#1F1F1F !important',
                    background: '#1F1F1F !important',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(31, 31, 31, 0.15)',
                    color: '#FFFFFF !important',
                    fontSize: '16px',
                    fontWeight: 600,
                    textTransform: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#e72f0d !important',
                      background: '#e72f0d !important',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(31, 31, 31, 0.25)'
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                    },
                    '&.Mui-disabled': {
                      opacity: 1,
                      cursor: 'not-allowed',
                      backgroundColor: '#1F1F1F !important',
                      background: '#1F1F1F !important',
                      color: 'rgba(255, 255, 255, 0.5) !important'
                    }
                  }}
                >
                  {loading ? 'Analyzuji...' : 'Získat právní odpověď'}
                </Button>
              </Box>
            </Box>
          ) : consultationPhase === 'clarifying' ? (
            /* Clarifying Questions Form */
            <Box>
              {domainsInfo.length > 0 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  {domainsInfo.length === 1 ? (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Klasifikované právní odvětví: {domainsInfo[0].domain}
                    </Typography>
                  ) : (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Klasifikovaná právní odvětví ({domainsCount}):
                      </Typography>
                      {domainsInfo.map((domainInfo, index) => (
                        <Typography key={index} variant="body2" sx={{ ml: 1, mb: 0.5 }}>
                          • <strong>{domainInfo.domain}</strong>: "{domainInfo.text.length > 100 
                            ? domainInfo.text.substring(0, 100) + '...' 
                            : domainInfo.text}"
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Alert>
              )}
              <Typography variant="h6" sx={{ 
                mb: 3, 
                fontWeight: 600,
                color: 'var(--liquid-gray-800)'
              }}>
                Aby vám mohl poskytnout nejpřesnější právní informace, potřebuji upřesnit několik detailů
              </Typography>
              
              <Box sx={{ 
                mb: 3, 
                p: 2.5, 
                background: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Vaše původní otázka:
                </Typography>
                <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                  {originalQuestion}
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleClarificationSubmit}>
                {clarifyingQuestions.map((question, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                      {index + 1}. {question}
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={clarifyingAnswers[index]}
                      onChange={(e) => {
                        const newAnswers = [...clarifyingAnswers];
                        newAnswers[index] = e.target.value;
                        setClarifyingAnswers(newAnswers);
                      }}
                      placeholder="Vaše odpověď..."
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          fontSize: '16px',
                          background: 'rgba(255, 255, 255, 0.8)',
                          '& fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.08)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1F1F1F !important',
                            borderWidth: '1px'
                          }
                        }
                      }}
                      disabled={loading}
                    />
                  </Box>
                ))}

                {error && (
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleNewQuestion}
                    disabled={loading}
                    sx={{
                      padding: '12px 28px',
                      borderRadius: '999px',
                      textTransform: 'none',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#1F1F1F',
                      borderColor: '#1F1F1F',
                      '&:hover': {
                        backgroundColor: 'rgba(31, 31, 31, 0.08)',
                        borderColor: '#1F1F1F'
                      }
                    }}
                  >
                    Začít znovu
                  </Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      // Set all answers to empty and proceed directly
                      const emptyAnswers = clarifyingQuestions.map(() => '');
                      setClarifyingAnswers(emptyAnswers);
                      
                      // Call the submission directly with empty answers
                      setLoading(true);
                      setError('');
                      setShowProcessingCard(true);
                      
                      // Realistic status updates based on what backend actually does
                      const statusUpdates = [
                        { phase: 'initializing' as const, message: 'Připravuji zpracování dotazu...', progress: 15, delay: 0 },
                        { phase: 'processing_context' as const, message: 'Zpracovávám kontext vašeho dotazu...', progress: 35, delay: 2000 },
                        { phase: 'ai_thinking' as const, message: 'AI analyzuje váš dotaz podle českého práva...', progress: 60, delay: 4000 },
                        { phase: 'preparing_response' as const, message: 'Připravuji právní odpověď...', progress: 85, delay: 7000 },
                        { phase: 'finalizing' as const, message: 'Dokončuji zpracování...', progress: 95, delay: 9000 }
                      ];
                      
                      // Set initial status
                      setProcessingStatus(statusUpdates[0]);
                      
                      // Schedule status updates
                      statusUpdates.slice(1).forEach(update => {
                        setTimeout(() => {
                          setProcessingStatus({
                            phase: update.phase,
                            message: update.message,
                            progress: update.progress
                          });
                        }, update.delay);
                      });

                      try {
                        const result = await api.post('/consultation/answer-clarification', {
                          conversation_id: conversationId,
                          answers: emptyAnswers,
                          language: 'cs'
                        });

                        const conversationMessages = [
                          { 
                            id: '1', 
                            type: 'user' as const, 
                            content: originalQuestion, 
                            timestamp: new Date() 
                          },
                          { 
                            id: '2', 
                            type: 'assistant' as const, 
                            content: result.data.response, 
                            timestamp: new Date(),
                            sources: result.data.sources,
                            confidence_level: result.data.confidence_level,
                            disclaimer: result.data.disclaimer
                          }
                        ];

                        setConversationHistory(conversationMessages);
                        setConsultationPhase('final');
                        setShowProcessingCard(false);
                      } catch (err) {
                        setError('Nepodařilo se získat právní analýzu. Zkuste to prosím znovu.');
                        setShowProcessingCard(false);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    sx={{
                      padding: '12px 28px',
                      borderRadius: '999px',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '15px',
                      background: 'rgba(255, 255, 255, 0.6)',
                      color: '#616161',
                      border: '1px solid rgba(255, 255, 255, 0.4)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.85)',
                        transform: 'translateY(-1px)',
                        borderColor: 'rgba(255, 255, 255, 0.6)',
                      }
                    }}
                  >
                    Přeskočit otázky
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <SendIcon />}
                    disabled={loading || clarifyingAnswers.some(answer => !answer.trim())}
                    sx={{
                      padding: '14px 36px',
                      backgroundColor: '#1F1F1F !important',
                      background: '#1F1F1F !important',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(31, 31, 31, 0.15)',
                      color: '#FFFFFF !important',
                      fontSize: '16px',
                      fontWeight: 600,
                      textTransform: 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: '#e72f0d !important',
                        background: '#e72f0d !important',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(31, 31, 31, 0.25)'
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                      },
                      '&.Mui-disabled': {
                        opacity: 1,
                        cursor: 'not-allowed',
                        backgroundColor: '#1F1F1F !important',
                        background: '#1F1F1F !important',
                        color: 'rgba(255, 255, 255, 0.5) !important'
                      }
                    }}
                  >
                    {loading ? 'Zpracovávám odpovědi...' : 'Získat právní analýzu'}
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            /* Conversation Display */
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Právní konzultace
                  </Typography>
                  {consultationPricing && (
                    <Typography variant="caption" sx={{ color: '#616161' }}>
                      Cena: {formatCurrency(consultationPricing.priceCZK)} Kč / {formatCurrency(consultationPricing.credits)} kreditů
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  onClick={handleNewQuestion}
                  sx={{
                    padding: '12px 20px',
                    color: '#1F1F1F',
                    borderColor: '#EDEDED',
                    borderRadius: '999px',
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#DCDCDC',
                      backgroundColor: '#F8F8F8',
                    }
                  }}
                >
                  Nová konzultace
                </Button>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Conversation History */}
              <Box sx={{ 
                maxHeight: '60vh', 
                overflowY: 'auto',
                mb: 3,
                px: 1
              }}>
                {conversationHistory.map((message, index) => (
                  <Box key={message.id} sx={{ mb: 3 }}>
                    {message.type === 'user' ? (
                      /* User Message */
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Paper sx={{ 
                          p: 2,
                          maxWidth: '80%',
                          bgcolor: '#e3f2fd',
                          borderRadius: 2,
                          borderTopRightRadius: 0
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                            Vaše otázka:
                          </Typography>
                          <Typography variant="body1">
                            {message.content}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {new Date(message.timestamp).toLocaleTimeString('cs-CZ', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </Typography>
                        </Paper>
                      </Box>
                    ) : (
                      /* Assistant Message */
                      <Box>
                        <Paper sx={{ 
                          p: 3,
                          maxWidth: '80%',
                          bgcolor: '#f0f7ff',
                          border: '1px solid #e3f2fd',
                          borderRadius: 2,
                          borderTopLeftRadius: 0
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: 'primary.main' }}>
                            AI Právní asistent:
                          </Typography>
                          <Box sx={{
                            lineHeight: 1.8,
                            '& p': { mb: 2 },
                            '& h1, & h2, & h3': { fontWeight: 600, mt: 2, mb: 1 },
                            '& h1': { fontSize: '1.5rem' },
                            '& h2': { fontSize: '1.3rem' },
                            '& h3': { fontSize: '1.1rem' },
                            '& ul, & ol': { pl: 3, mb: 2 },
                            '& li': { mb: 0.5 },
                            '& code': {
                              backgroundColor: '#f5f5f5',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontFamily: 'monospace'
                            },
                            '& pre': {
                              backgroundColor: '#f5f5f5',
                              padding: 2,
                              borderRadius: 1,
                              overflow: 'auto'
                            },
                            '& strong': {
                              fontWeight: 700
                            },
                            '& em': { fontStyle: 'italic' }
                          }}>
                            {(() => {
                              // Render assistant content without yellow highlighting on consultation page
                              return <ReactMarkdown rehypePlugins={[rehypeRaw]}>{message.content}</ReactMarkdown>;
                            })()}
                          </Box>
                          
                          {message.sources && message.sources.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                Zdroje:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {message.sources.map((source, idx) => {
                                  // Extract clean URL: if source contains " - ", take part after it, otherwise use whole source
                                  const cleanUrl = source.includes(' - ') ? source.split(' - ').pop()?.trim() || source : source;
                                  const displayText = cleanUrl.length > 50 ? cleanUrl.substring(0, 50) + '...' : cleanUrl;

                                  return (
                                    <Box
                                      key={idx}
                                      component="a"
                                      href={cleanUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        bgcolor: '#e0e0e0',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        fontSize: '11px',
                                        textDecoration: 'none',
                                        color: '#1F1F1F',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                          bgcolor: '#d32f2f',
                                          color: '#FFFFFF',
                                          transform: 'translateY(-1px)'
                                        }
                                      }}
                                    >
                                      {displayText}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                          
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {new Date(message.timestamp).toLocaleTimeString('cs-CZ', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </Typography>
                        </Paper>
                        
                        {/* Verification for this specific message */}
                        {message.verificationResult && (
                          <Box sx={{ ml: 2, mt: 1 }}>
                            <LegalVerificationPanel
                              verificationResult={message.verificationResult}
                              isVerifying={false}
                              expanded={false}
                            />
                          </Box>
                        )}
                        
                        {/* Disclaimer Alert */}
                        {index === conversationHistory.length - 1 && message.disclaimer && (
                          <Alert severity="warning" sx={{ mt: 2, maxWidth: '80%', borderRadius: 2 }}>
                            <Typography variant="caption">
                              <strong>Upozornění:</strong> {message.disclaimer}
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
                
                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      AI právní asistent připravuje odpověď...
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Follow-up Question Input - Only show in final phase */}
              {consultationPhase === 'final' && (
                <Box component="form" onSubmit={handleFollowUpSubmit}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Máte další otázku k této konzultaci?
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={followUpQuestion}
                    onChange={(e) => setFollowUpQuestion(e.target.value)}
                    placeholder="Například: Můžete mi to vysvětlit podrobněji? Jaké jsou další možnosti?"
                    variant="outlined"
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '16px',
                        '&.Mui-focused fieldset': {
                          borderColor: '#1F1F1F !important',
                          borderWidth: '1px'
                        }
                      }
                    }}
                    disabled={loading}
                  />
                  
                  {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      {error}
                    </Alert>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={loading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <SendIcon />}
                      disabled={loading || !followUpQuestion.trim()}
                      sx={{
                        padding: '14px 36px',
                        backgroundColor: '#1F1F1F !important',
                        background: '#1F1F1F !important',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(31, 31, 31, 0.15)',
                        color: '#FFFFFF !important',
                        fontSize: '16px',
                        fontWeight: 600,
                        textTransform: 'none',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          backgroundColor: '#e72f0d !important',
                          background: '#e72f0d !important',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 20px rgba(31, 31, 31, 0.25)'
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)'
                        },
                        '&.Mui-disabled': {
                          opacity: 1,
                          cursor: 'not-allowed',
                          backgroundColor: '#1F1F1F !important',
                          background: '#1F1F1F !important',
                          color: 'rgba(255, 255, 255, 0.5) !important'
                        }
                      }}
                    >
                      {loading ? 'Zpracovávám...' : 'Poslat další otázku'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Legal Verification Panel */}
        {response && (
          <Box sx={{ mt: 2 }}>
            <LegalVerificationPanel
              verificationResult={verificationResult}
              isVerifying={isVerifying}
              expanded={false}
            />
          </Box>
        )}

        {/* Note: Web search is now integrated automatically in the backend */}

        </Box>
      </Box>
    </Box>
  );
};

export default ConsultationPage;
